/**
 * screens/settings/team.tsx — Team & Roles (P-003).
 * Faithful RN port of design/org.jsx TeamPage + InviteModal + ConfirmModal.
 * ADMIN-only. Members tab (inline role select, deactivate/reactivate, last-admin
 * guard) + Pending invites tab. Wired to platformApi (invitations/members).
 */

import React, { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, Tag, StatusBadge } from '../../components/platform/primitives';
import { PButton, PSelect, PTextInput, PTextarea, PModal } from '../../components/platform/controls';
import Tabs from '../../components/tabs';
import Avatar from '../../components/avatar';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW } from '../../utils/platformTokens';
import { ROLES, type Member, type Invitation, type Role } from '../../data/platformSeed';
import { createInvitation, resendInvitation, cancelInvitation } from '../../services/platform/platformApi';
import { formatMembershipWindow, matchesMemberStatusFilter, type MemberStatusFilter } from '../../utils/membershipWindow';

const ROLE_OPTS = [
  { value: 'ADMIN', label: 'ADMIN — full access' },
  { value: 'EDITOR', label: 'EDITOR — manage data' },
  { value: 'VIEWER', label: 'VIEWER — read only' },
];

const InviteModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { state, setState } = usePlatform();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [roleSel, setRoleSel] = useState<Role>('EDITOR');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const dup = state.invitations.some((i) => i.email.toLowerCase() === email.trim().toLowerCase());

  const send = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr('Enter a valid email.'); return; }
    if (dup) { setErr('Already invited.'); return; }
    await createInvitation(state.org.name, { email: email.trim(), role: roleSel, note }).catch(() => undefined);
    const inv: Invitation = { id: 'i' + Date.now(), email: email.trim(), role: roleSel, invited: 'Just now', expiresInDays: 7 };
    setState((s) => ({ ...s, invitations: [inv, ...s.invitations] }));
    toast.success(`Invitation sent to ${email.trim()}.`);
    setEmail(''); setNote(''); setErr(''); onClose();
  };

  return (
    <PModal
      open={open}
      onClose={onClose}
      title="Invite a member"
      footer={<>
        <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
        <PButton kind="accent" icon="mail" onPress={send}>Send invitation</PButton>
      </>}
    >
      <View style={{ gap: 14 }}>
        <PTextInput label="Email address" placeholder="teammate@company.com" value={email}
          onChangeText={(v) => { setEmail(v); setErr(''); }} keyboardType="email-address"
          error={err && err !== 'Already invited.' ? err : undefined} />
        <PSelect label="Role" value={roleSel} onChange={(v) => setRoleSel(v as Role)} options={ROLE_OPTS} />
        <PTextarea label="Personal note (optional)" placeholder="Hey! Join our workspace…" value={note} onChangeText={setNote} rows={2} />
      </View>
    </PModal>
  );
};

const ConfirmModal: React.FC<{
  open: boolean; onClose: () => void; onConfirm: () => void; title: string; body: string; confirmLabel: string;
}> = ({ open, onClose, onConfirm, title, body, confirmLabel }) => {
  const { theme } = useTheme();
  return (
    <PModal open={open} onClose={onClose} size="sm" title={title}
      footer={<>
        <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
        <PButton kind="danger" onPress={onConfirm}>{confirmLabel}</PButton>
      </>}>
      <Text style={{ fontSize: FS.md, color: theme.colors.ink2, lineHeight: 20 }}>{body}</Text>
    </PModal>
  );
};

const TeamScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState, role } = usePlatform();
  const toast = useToast();
  const [tab, setTab] = useState('active');
  // CH-U2 — Active / Inactive / All, defaulting to Active. Former members are
  // no longer deleted (CC-002 CH-3), so without a default the roster would grow
  // to include everyone who ever worked here.
  const [statusFilter, setStatusFilter] = useState<MemberStatusFilter>('active');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState<Member | null>(null);

  if (role !== 'ADMIN') return <PermissionDenied />;

  const activeAdmins = state.members.filter((m) => m.role === 'ADMIN' && m.status === 'active');
  const setRoleFor = (id: string, newRole: Role) => {
    setState((s) => ({ ...s, members: s.members.map((m) => (m.id === id ? { ...m, role: newRole } : m)) }));
    toast.success('Role updated.');
  };
  const toggleActive = (m: Member) => {
    const deactivating = m.status === 'active';
    const now = new Date().toISOString();
    setState((s) => ({
      ...s,
      members: s.members.map((x) =>
        x.id === m.id
          ? deactivating
            // Close the window rather than dropping the row — the console
            // mirrors the API's deactivate-only lifecycle (CC-002 CH-3).
            ? { ...x, status: 'deactivated' as const, deactivatedAt: now }
            // Reactivating OPENS A NEW WINDOW: a fresh joinedAt, and the old
            // end date cleared. Keeping the previous joinedAt would report the
            // rehire as continuous service.
            : { ...x, status: 'active' as const, joinedAt: now, deactivatedAt: undefined }
          : x,
      ),
    }));
    // CH-U2's exact copy. The second sentence is the point: it tells the admin
    // the revocation already happened, so they do not go hunting for a
    // "sign out everywhere" button that does not exist.
    toast.success(
      deactivating
        ? 'Member deactivated. Their sessions have been signed out.'
        : `${m.name} reactivated.`,
    );
    setConfirm(null);
  };
  const resend = (inv: Invitation) => { resendInvitation(state.org.name, inv.id).catch(() => undefined); toast.success(`Invitation to ${inv.email} resent.`); };
  const cancelInv = (inv: Invitation) => {
    cancelInvitation(state.org.name, inv.id).catch(() => undefined);
    setState((s) => ({ ...s, invitations: s.invitations.filter((i) => i.id !== inv.id) }));
    toast.success('Invitation canceled.');
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Team & Roles"
        sub="Manage who can act in your workspace and what they can do."
        action={<PButton kind="accent" icon="plus" onPress={() => setInviteOpen(true)}>Invite member</PButton>}
      />

      <Tabs value={tab} onChange={setTab} tabs={[
        // Counts the ACTIVE roster — 'Members' meaning people who can act today,
        // not everyone who ever could.
        { id: 'active', label: 'Members', count: state.members.filter((m) => m.status === 'active').length },
        { id: 'pending', label: 'Pending invites', count: state.invitations.length },
      ]} />

      <View style={{ height: 16 }} />

      {tab === 'active' ? (
        <Panel bodyStyle={{ padding: 0 }}>
          {/* CH-U2 — Active / Inactive / All. Deactivated members are kept
              forever now, so the roster needs a way to show them WITHOUT
              making them the default view. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14 }}>
            <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Show</Text>
            <PSelect
              size="sm"
              width={140}
              value={statusFilter}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'all', label: 'All' },
              ]}
              onChange={(v) => setStatusFilter(v as MemberStatusFilter)}
            />
          </View>
          <DataTable<Member>
            minWidth={720}
            keyExtractor={(m) => m.id}
            rows={state.members.filter((m) => matchesMemberStatusFilter(m, statusFilter))}
            empty={
              <Text style={{ color: c.ink3 }}>
                {statusFilter === 'inactive'
                  ? 'No deactivated members.'
                  : 'No members match this filter.'}
              </Text>
            }
            columns={[
              {
                key: 'member', header: 'Member', width: 240,
                render: (m) => (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <Avatar name={m.name} size="sm" />
                    <View>
                      <Text style={{ fontWeight: FW.medium, color: c.ink, fontSize: FS.md }}>{m.name}</Text>
                      <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{m.email}</Text>
                    </View>
                  </View>
                ),
              },
              {
                key: 'role', header: 'Role', width: 150,
                render: (m) => {
                  const isLastAdmin = m.role === 'ADMIN' && activeAdmins.length === 1 && m.status === 'active';
                  return (
                    <PSelect size="sm" width={130} value={m.role}
                      options={ROLES.map((r) => ({ value: r, label: r }))}
                      disabled={isLastAdmin || m.status === 'deactivated'}
                      onChange={(v) => setRoleFor(m.id, v as Role)} />
                  );
                },
              },
              { key: 'seen', header: 'Last seen', width: 120, render: (m) => <Text style={{ color: c.ink3, fontSize: FS.md }}>{m.lastSeen}</Text> },
              {
                key: 'status', header: 'Status', width: 210,
                render: (m) => (
                  <View style={{ gap: 2 }}>
                    <StatusBadge status={m.status} />
                    {/* CH-U2 — the employment window. Rendering it is what makes
                        the confirm copy's "their history is retained" a visible
                        fact rather than a promise. */}
                    {m.status !== 'active' && !!formatMembershipWindow(m) && (
                      <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{formatMembershipWindow(m)}</Text>
                    )}
                  </View>
                ),
              },
              {
                key: 'actions', header: 'Actions', align: 'right',
                render: (m) => {
                  const isLastAdmin = m.role === 'ADMIN' && activeAdmins.length === 1 && m.status === 'active';
                  if (isLastAdmin) return <PButton size="sm" kind="ghost" disabled>Deactivate</PButton>;
                  return m.status === 'active'
                    ? <PButton size="sm" kind="ghost" onPress={() => setConfirm(m)}>Deactivate</PButton>
                    : <PButton size="sm" kind="ghost" onPress={() => toggleActive(m)}>Reactivate</PButton>;
                },
              },
            ]}
          />
        </Panel>
      ) : (
        <Panel bodyStyle={{ padding: 0 }}>
          <DataTable<Invitation>
            minWidth={680}
            keyExtractor={(i) => i.id}
            rows={state.invitations}
            empty={<Text style={{ color: c.ink3 }}>No pending invitations.</Text>}
            columns={[
              { key: 'email', header: 'Email', width: 220, render: (i) => <Text style={{ fontFamily: theme.font.mono, color: c.ink, fontSize: FS.md }}>{i.email}</Text> },
              { key: 'role', header: 'Role', width: 100, render: (i) => <Tag tone="neutral">{i.role}</Tag> },
              { key: 'invited', header: 'Invited', width: 120, render: (i) => <Text style={{ color: c.ink3, fontSize: FS.md }}>{i.invited}</Text> },
              { key: 'expires', header: 'Expires', width: 110, render: (i) => <StatusBadge status={i.expiresInDays <= 1 ? 'warn' : 'pending'} label={`in ${i.expiresInDays}d`} /> },
              {
                key: 'actions', header: 'Actions', align: 'right',
                render: (i) => (
                  <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'flex-end' }}>
                    <PButton size="sm" kind="ghost" onPress={() => resend(i)}>Resend</PButton>
                    <PButton size="sm" kind="ghost" onPress={() => cancelInv(i)}>Cancel</PButton>
                  </View>
                ),
              },
            ]}
          />
        </Panel>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={`Deactivate ${confirm?.name}?`}
        confirmLabel="Deactivate"
        /* CH-U2's mandated copy. "Their history is retained" is literally true
           now (CC-002 CH-3 keeps the row), and the members list renders the
           window, so the sentence is checkable rather than reassuring. */
        body={'Deactivate this member? They lose access immediately. Their history is retained.'}
        onConfirm={() => confirm && toggleActive(confirm)}
      />
    </ScrollView>
  );
};

export default TeamScreen;
